import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Aviso from './Aviso';
import Carregando from './Carregando';
import CampoNumero from './CampoNumero';
import SemDado from './SemDado';
import {
  indicadoresDoDono, limitesDaOperacao, resultadoOperacional, salvarConfigFinanceira,
} from '../lib/consultas';
import { brl } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

// A saúde do negócio (migração 043) — a seção do Painel que responde às
// perguntas de DONO: quanto sobra de verdade (margens, EBIT), quanto devo ×
// quanto me devem, quanto vale o estoque, e a pergunta que motivou tudo:
// "se eu aplicasse R$ X, em quanto tempo volta?".
//
// As decisões de cálculo moram em core/sql/functions/saude_negocio.sql — a
// tela só mostra, com as ressalvas que os números trazem (cobertura do CMV,
// meses de história do ROIC, peças sem custo fora da avaliação).

const pct = (n: number | null) => (n === null ? '—' : `${String(n).replace('.', ',')}%`);

/** O número do cartão (Grafite): mono, algarismos alinhados, claro — e o
 *  degrau de tamanho acompanha a coluna (duas até o tablet, quatro no
 *  computador), para "R$ 182.400,00" nunca sair cortado. */
const VALOR = 'numero text-base font-medium whitespace-nowrap text-paper sm:text-xl lg:text-lg xl:text-2xl';

export default function SaudeNegocio() {
  const cliente = useQueryClient();
  const ind = useQuery({ queryKey: ['saude', 'indicadores'], queryFn: indicadoresDoDono });
  const meses = useQuery({ queryKey: ['saude', 'resultado'], queryFn: () => resultadoOperacional(12) });
  const limites = useQuery({ queryKey: ['limites-operacao'], queryFn: limitesDaOperacao });

  const [editandoConfig, setEditandoConfig] = useState(false);
  const [aplicaria, setAplicaria] = useState<number | null>(null);

  const d = ind.data;
  const mesAtual = meses.data?.at(-1) ?? null;
  const comAlicuota = limites.data?.aliquota_imposto_pct != null;
  /** O líquido do mês COM a mercadoria: EBIT real menos o imposto que saiu do
   *  caixa. Nulo sem régua — e nulo é traço na tela, nunca o número sem custo. */
  const liquidoReal = mesAtual?.ebit_real == null
    ? null
    : Math.round((Number(mesAtual.ebit_real) - Number(mesAtual.imposto_pago)) * 100) / 100;

  // O payback: a pergunta do dono, respondida com o EBIT médio real.
  const payback = aplicaria && d?.ebit_medio_mensal && d.ebit_medio_mensal > 0
    ? aplicaria / d.ebit_medio_mensal
    : null;

  return (
    <section className="space-y-3">
      <h2 className="rotulo">Saúde do negócio</h2>
      {(ind.isPending || meses.isPending) && <Carregando />}
      {ind.error ? <Aviso tipo="erro">{mensagemDeErro(ind.error)}</Aviso> : null}

      {d && mesAtual && (
        <>
          {/* Quatro colunas só a partir do computador (Grafite): o número em
              mono é mais largo, e a 640 px quatro colunas cortariam
              "R$ 182.400,00". No celular e no tablet, duas. */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
            {/* 28/09/2026 — ESTES DOIS CARTÕES MOSTRAVAM A MARGEM SEM A
                MERCADORIA: 99,8% de margem bruta e R$ 47.980,96 de EBIT em
                agosto, numa loja de autopeças. O boleto do fornecedor é tirado
                das despesas de propósito (é estoque, não despesa) e o custo
                cadastrado cobre 0,5% das vendas — a mercadoria saía de graça.
                Agora vem do `_real`, que é a mesma medição com a régua do dono
                sobre a venda que não tem custo. Sem régua, traço. */}
            <div className="cartao p-3">
              <p className="rotulo">Margem bruta (mês)</p>
              <p className={VALOR}>
                {mesAtual.margem_bruta_real_pct == null ? <SemDado /> : pct(mesAtual.margem_bruta_real_pct)}
              </p>
              <p className="text-xs text-dim">
                {mesAtual.mercadoria == null
                  ? 'falta o custo da mercadoria no cenário'
                  : `${brl(Number(mesAtual.mercadoria))} de mercadoria · ${
                      pct(mesAtual.mercadoria_estimada_pct)} pela régua`}
              </p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">EBIT (mês)</p>
              <p className={VALOR}>
                {mesAtual.ebit_real == null ? <SemDado /> : brl(Number(mesAtual.ebit_real))}
              </p>
              <p className="text-xs text-dim">antes de impostos, com a mercadoria</p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">Margem líquida (mês)</p>
              {/* O líquido sai do EBIT com mercadoria, menos o imposto PAGO. */}
              <p className={VALOR}>
                {liquidoReal == null || Number(mesAtual.receita) <= 0
                  ? <SemDado />
                  : pct(Math.round(liquidoReal / Number(mesAtual.receita) * 1000) / 10)}
              </p>
              <p className="text-xs text-dim">
                {liquidoReal == null ? 'falta o custo da mercadoria' : brl(liquidoReal)}
                {comAlicuota && mesAtual.imposto_simulado != null
                  && ` · simulado −${brl(Number(mesAtual.imposto_simulado))}`}
              </p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">ROIC (anualizado)</p>
              <p className={VALOR}>{d.roic_pct == null ? <SemDado /> : pct(d.roic_pct)}</p>
              <p className="text-xs text-dim">
                base de {d.meses_de_historia} {d.meses_de_historia === 1 ? 'mês' : 'meses'}
                {d.capital_investido == null && ' · sem capital digitado'}
              </p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">Estoque avaliado</p>
              <p className={VALOR}>{brl(Number(d.estoque_valor))}</p>
              {d.estoque_pecas_sem_custo > 0 && (
                <p className="flex items-center gap-1.5 text-xs text-mute">
                  <span className="ponto text-alerta" aria-hidden="true" />
                  <span><span className="numero">{d.estoque_pecas_sem_custo}</span> peça(s) sem custo fora da conta</span>
                </p>
              )}
            </div>
            <div className="cartao p-3">
              <p className="rotulo">Dívida líquida</p>
              <p className={VALOR}>{brl(Number(d.divida_liquida))}</p>
              <p className="text-xs text-dim">
                deve {brl(Number(d.a_pagar))} · a receber {brl(Number(d.a_receber))}
              </p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">Patrimônio</p>
              <p className={VALOR}>{brl(Number(d.patrimonio))}</p>
              <p className="text-xs text-dim">estoque + a receber − a pagar</p>
            </div>
            <div className="cartao p-3">
              <p className="rotulo">Giro do estoque</p>
              <p className={VALOR}>
                {d.giro_estoque != null ? `${String(d.giro_estoque).replace('.', ',')}×/ano` : <SemDado />}
              </p>
              <p className="text-xs text-dim">
                {d.meses_de_estoque != null
                  ? `${String(d.meses_de_estoque).replace('.', ',')} meses de estoque`
                  : 'sem CMV suficiente'}
              </p>
            </div>
          </div>

          {/* O simulador — a pergunta que motivou o módulo. */}
          <div className="cartao p-4">
            <p className="rotulo">Se eu aplicasse…</p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <CampoNumero className="campo w-40 py-2" valor={aplicaria}
                           placeholder="R$ 0,00" aria-label="Valor a aplicar"
                           onValor={setAplicaria} />
              {payback != null ? (
                <p className="text-sm">
                  volta em <strong>{Math.ceil(payback)} {Math.ceil(payback) === 1 ? 'mês' : 'meses'}</strong>
                  <span className="ml-1 text-xs text-dim">
                    (EBIT médio de {brl(Number(d.ebit_medio_mensal))}/mês, base de {d.meses_de_historia} m.)
                  </span>
                </p>
              ) : aplicaria ? (
                <p className="text-sm text-dim">sem EBIT médio positivo, não há payback honesto</p>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-dim">
              Conta simples de retorno: o valor dividido pelo EBIT médio mensal —
              assume que o negócio segue como está, sem o efeito do próprio investimento.
            </p>
          </div>

          {/* Resultado econômico, mês a mês — ao lado da tabela de caixa que já
              existe: são duas histórias (o que a loja GEROU × o que ENTROU). */}
          <div className="cartao overflow-x-auto p-4">
            <p className="rotulo mb-2">Resultado econômico (12 meses)</p>
            {/* O espaço entre as colunas (revisão do #359): em mono, a 390 px,
                os valores grudavam um no outro. */}
            <table className="w-full text-right text-xs [&_td]:px-2 [&_th]:px-2 [&_td:first-child]:pl-0 [&_th:first-child]:pl-0 [&_td:last-child]:pr-0 [&_th:last-child]:pr-0">
              <thead>
                <tr className="text-dim">
                  <th className="py-1 text-left font-normal">Mês</th>
                  <th className="font-normal">Receita</th>
                  <th className="font-normal">Mercadoria</th>
                  <th className="font-normal">M. bruta</th>
                  <th className="font-normal">Despesas</th>
                  <th className="font-normal">EBIT</th>
                  <th className="font-normal">Líquido</th>
                </tr>
              </thead>
              <tbody>
                {(meses.data ?? []).filter((m) => Number(m.receita) !== 0 || Number(m.despesas) !== 0).map((m) => (
                  <tr key={m.competencia} className="numero border-t border-fio">
                    <td className="py-1.5 text-left">{m.competencia.split('-').reverse().join('/')}</td>
                    <td>{brl(Number(m.receita))}</td>
                    {/* Mercadoria, não CMV: é o cadastrado MAIS a régua sobre a
                        venda sem custo. A coluna que dizia "CMV" mostrava R$
                        117 num mês de R$ 51 mil de venda. */}
                    <td>{m.mercadoria == null ? '—' : brl(Number(m.mercadoria))}</td>
                    <td>{m.margem_bruta_real_pct == null ? '—' : pct(m.margem_bruta_real_pct)}</td>
                    <td>{brl(Number(m.despesas))}</td>
                    <td className="font-medium text-paper">
                      {m.ebit_real == null ? '—' : brl(Number(m.ebit_real))}
                    </td>
                    <td>
                      {m.ebit_real == null
                        ? '—'
                        : brl(Math.round((Number(m.ebit_real) - Number(m.imposto_pago)) * 100) / 100)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* As duas configurações que alimentam tudo isto. */}
          <div className="cartao p-4">
            {editandoConfig ? (
              <ConfigFinanceira
                capital={limites.data?.capital_investido ?? null}
                aliquota={limites.data?.aliquota_imposto_pct ?? null}
                multCoringa={Number(limites.data?.coringa_multiplicador ?? 2)}
                aoFechar={() => setEditandoConfig(false)}
                aoSalvar={() => {
                  setEditandoConfig(false);
                  cliente.invalidateQueries({ queryKey: ['saude'] });
                  cliente.invalidateQueries({ queryKey: ['limites-operacao'] });
                }}
              />
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <p className="text-mute">
                  Capital investido: <strong>{d.capital_investido != null ? brl(Number(d.capital_investido)) : 'não informado'}</strong>
                  {' · '}Alíquota de simulação: <strong>{comAlicuota ? pct(limites.data!.aliquota_imposto_pct) : 'não configurada'}</strong>
                </p>
                <button type="button" className="inline-flex min-h-11 items-center text-xs text-dim underline sm:min-h-0"
                        onClick={() => setEditandoConfig(true)}>
                  configurar
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ConfigFinanceira({ capital, aliquota, multCoringa, aoFechar, aoSalvar }: {
  capital: number | null;
  aliquota: number | null;
  multCoringa: number;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [cap, setCap] = useState<number | null>(capital);
  const [ali, setAli] = useState<number | null>(aliquota);
  const [mult, setMult] = useState<number | null>(multCoringa);

  const salvar = useMutation({
    mutationFn: () => salvarConfigFinanceira({
      capitalInvestido: cap, aliquotaImpostoPct: ali,
      // Vazio volta para a regra da casa (x2) — multiplicador não tem "sem".
      coringaMultiplicador: mult != null && mult > 0 ? mult : 2,
    }),
    onSuccess: aoSalvar,
  });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="cfg-capital">Capital investido (R$)</label>
          <CampoNumero id="cfg-capital" className="campo" valor={cap} onValor={setCap} />
          <p className="mt-1 text-xs text-dim">
            O que você pôs no negócio além do que o sistema enxerga. Entra no
            capital do ROIC. Vazio = não informado.
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="cfg-aliquota">Alíquota de imposto (%)</label>
          <CampoNumero id="cfg-aliquota" className="campo" valor={ali} onValor={setAli} />
          <p className="mt-1 text-xs text-dim">
            Só SIMULAÇÃO (na ficha da peça e no resultado). O imposto pago de
            verdade continua sendo o lançado no caixa. Vazio = sem simulação.
          </p>
        </div>
        <div>
          <label className="rotulo" htmlFor="cfg-mult">Peça diversa: custo ×</label>
          <CampoNumero id="cfg-mult" className="campo" valor={mult} onValor={setMult} />
          <p className="mt-1 text-xs text-dim">
            A regra da casa: o preço sugerido da peça diversa é o custo digitado
            vezes este número. Hoje: ×2. Vazio volta para 2.
          </p>
        </div>
      </div>
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending} onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
